/**
 * Entry point.
 *
 * Deliberately thin: mount, register the service worker, and hand off. The
 * SSO hint is captured by a side-effect import that must run before anything
 * navigates, so it is first.
 */
import './lib/sso';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './design/tokens.css';
import './design/base.css';
import './design/app.css';

import { App } from './App';
import { registerServiceWorker } from './lib/serviceWorker';

/*
 * Recover from a stale build before React ever sees it.
 *
 * Vite fires this when a route's chunk fails to preload — which after a deploy
 * means the filename in this page's index.html no longer exists. Handling it
 * here reloads silently, so the reader gets the screen they tapped rather than
 * an error they then have to dismiss. The boundary still covers the case where
 * the failure surfaces during render instead.
 *
 * Guarded by a session flag: a genuinely broken build must not become a reload
 * loop.
 */
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  try {
    if (sessionStorage.getItem('ss.chunkReloaded')) return;
    sessionStorage.setItem('ss.chunkReloaded', '1');
  } catch {
    return;
  }
  window.location.reload();
});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// After mount, so registration never competes with the first paint.
registerServiceWorker();
